import React from 'react'
import AntdButton from 'antd/es/button'
import type { ButtonProps } from 'antd/es/button'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import { Button as ShadcnSeamButton } from './Button.shadcn'

type ButtonGroupProps = React.ComponentProps<typeof AntdButton.Group>

/**
 * Compat seam leaf for antd's `antd/es/button` — and the FIRST component put
 * behind the design toggle.
 *
 * A LEAF, not the `src/antd` barrel, on purpose (PRO-130): the barrel's
 * `export * from 'antd'` pulls antd/es/index.js — the whole component barrel —
 * into whatever chunk imports it, and the call sites for this module are
 * boot-static. `update-design` ships a real seam at this same path, with the
 * same shape — a NAMED `Button` and no default — so a redirected call site
 * needs no further change when that lands. The named-only shape is deliberate
 * mirroring, not an oversight: adding a default here that the merge target
 * does not have would break any call site that then adopted it.
 *
 * ---------------------------------------------------------------------------
 * THE SWITCH, and its safety property
 *
 * `useDesignMode()` returns `'antd'` for everyone who has not picked "New
 * design" under Theme — which is the shipped default, so it is every page load
 * of every customer who has not opted in. In the `'antd'` branch this component renders antd's own
 * Button with the props it was given and the ref it was given: same element,
 * same class names, same DOM. The only difference from
 * `export { default as Button } from 'antd/es/button'` — which is what this
 * file was — is one extra component in the React tree, which renders nothing
 * of its own. `__tests__/button-seam-parity.test.tsx` asserts that byte for
 * byte across a prop matrix, and it is the test to keep green: `development`
 * continuing to ship antd unchanged to customers is the whole premise of
 * shipping shadcn one component at a time.
 *
 * WHY BUTTON WAS CHOSEN FIRST. It is the cleanest thing in the seam to prove:
 * ~600 files already import `Button` from `src/antd`, so flipping the control
 * repaints nearly every screen at once and a switch that only half-works is
 * impossible to miss; the reference branch's visual-defect survey lists no
 * defect against it, so
 * the port imports nothing that is still being fixed over there; and the
 * reference implementation is one self-contained file with a handful of
 * dependencies, so the shadcn foundation it needs is the minimum that any
 * later port would need anyway.
 *
 * antd's two statics are carried across explicitly, NOT by copying antd's
 * component object: `Object.assign(dispatcher, AntdButton)` would also copy a
 * forwardRef object's `$$typeof` and `render` and turn this into something
 * React cannot render. `Group` is antd's segmented row — it dispatches on the
 * mode exactly as the button itself does, so the seam cannot end up
 * half-switched the day a call site appears; `__ANT_BUTTON` is read
 * by `Input.Search` (`antd/es/input/Search.js`) to decide whether the
 * `enterButton` it was handed is one of antd's, so dropping it would change
 * behaviour with the toggle OFF.
 * ------------------------------------------------------------------------- */
const ButtonDispatcher = React.forwardRef<HTMLElement, ButtonProps>(
  (props, ref) => {
    const designMode = useDesignMode()

    if (designMode === 'shadcn') {
      return (
        <ShadcnSeamButton
          {...props}
          ref={ref as React.Ref<HTMLButtonElement>}
        />
      )
    }

    return <AntdButton {...props} ref={ref} />
  }
)

ButtonDispatcher.displayName = 'Button'

const ButtonGroupDispatcher = (props: ButtonGroupProps) => {
  const designMode = useDesignMode()

  if (designMode === 'shadcn') return <ShadcnSeamButton.Group {...props} />

  return <AntdButton.Group {...props} />
}

ButtonGroupDispatcher.displayName = 'Button.Group'

export const Button = Object.assign(ButtonDispatcher, {
  Group: ButtonGroupDispatcher,
  __ANT_BUTTON: true,
})
